import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Edit2, X } from 'lucide-react';
import { AnnotatedSection } from '@/types/dora-annotations';
import { AnnotationCard } from './AnnotationCard';

interface EditableSectionProps {
  section: AnnotatedSection;
  editedText?: string;
  onEdit: (text: string) => void;
  onApplySuggestion: (text: string) => void;
}

export function EditableSection({
  section,
  editedText,
  onEdit,
  onApplySuggestion
}: EditableSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localText, setLocalText] = useState(editedText || section.modified_text);

  // Group annotations by type and severity
  const criticalAnnotations = section.annotations.filter(a => a.severity === 'CRITICAL');
  const hasIssues = section.annotations.some(a => a.type === 'semantic_issue');
  const hasFills = section.annotations.some(a => a.type === 'filled_blank');

  return (
    <div className="mb-8 relative">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg">{section.section_title}</h3>
        <div className="flex items-center gap-2">
          {hasFills && (
            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400">
              ✅ Filled
            </Badge>
          )}
          {criticalAnnotations.length > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400">
              ⚠️ {criticalAnnotations.length} issue{criticalAnnotations.length > 1 ? 's' : ''}
            </Badge>
          )}
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? <X className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Section content - editable or display */}
      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={localText}
            onChange={(e) => setLocalText(e.target.value)}
            className="min-h-[120px] font-serif text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => {
              onEdit(localText);
              setIsEditing(false);
            }}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              setLocalText(editedText || section.modified_text);
              setIsEditing(false);
            }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-sm leading-relaxed whitespace-pre-wrap font-serif">
            {localText}
          </p>
        </div>
      )}

      {/* Inline annotations below the text */}
      {section.annotations.map((annotation, idx) => (
        <AnnotationCard
          key={idx}
          annotation={annotation}
          onApply={() => {
            if (annotation.semantic_issue) {
              setLocalText(annotation.semantic_issue.recommendation);
              onApplySuggestion(annotation.semantic_issue.recommendation);
            }
          }}
          onEdit={(editedSuggestion) => {
            setLocalText(editedSuggestion);
            onEdit(editedSuggestion);
          }}
        />
      ))}
    </div>
  );
}
